import type { VerseTopic } from "./types";

export const topic: VerseTopic = {
  slug: "bible-verses-about-protection",
  title: "10 Bible verses about protection, and what each one guarantees",
  h1: "Bible verses about protection",
  description:
    "Ten passages from the World English Bible with a note on each — read carefully, because a protection verse taken as insurance fails at the worst possible moment.",
  targets: [
    "bible verses about protection",
    "scripture for protection and safety",
    "verses about god's protection",
  ],
  published: "2026-09-03",
  standfirst:
    "Nearly every image in this collection is a shelter, and a shelter is only useful because there is something outside it. That is the reading these verses can survive.",
  takeaways: [
    "The imagery is overwhelmingly defensive architecture — rock, fortress, tower, hiding place, shield — and every one of those assumes a live threat rather than its absence.",
    "Psalm 91:15 is the qualifier the whole category needs: I will be with him in trouble, which concedes the trouble in the psalm's own voice.",
    "Read as a guarantee of physical safety, these verses fail on contact with any hospital ward, and the person they fail for concludes their faith was defective.",
    "Read as refuge inside danger, they are sayable at a funeral — which is the test of whether a promise was understood.",
  ],
  intro: [
    {
      type: "p",
      text: "Protection is thirteenth on the most-searched topic list of the largest topical Bible site, and it is one of the categories where the reading matters most. Almost every passage below uses the vocabulary of fortification, and fortifications exist because of what is outside them.",
    },
    {
      type: "p",
      text: "That is not a downgrade. It is what the texts say, and it is the version that keeps working — the same argument {Psalm 91} makes about itself in verse 15, and the reason the New Testament's only quotation of that psalm is a warning about how it gets misused.",
      links: { "Psalm 91": "/verse/psalm-91" },
    },
    {
      type: "note",
      text: "Quotations are from the World English Bible, which is in the public domain.",
    },
  ],
  verses: [
    {
      verse: {
        text: "Yahweh is my rock, my fortress, and my deliverer; my God, my rock, in whom I take refuge; my shield, and the horn of my salvation, my high tower.",
        ref: "Psalm 18:2",
      },
      note: "Seven images stacked in one verse, and every one is defensive architecture. The psalm's heading places it on the day David was delivered from Saul, after years of being hunted — so the fortress language comes from somebody who had spent a long time in caves. None of it describes the pursuit not happening.",
    },
    {
      verse: {
        text: "Yahweh's name is a strong tower: the righteous run to him, and are safe.",
        ref: "Proverbs 18:10",
      },
      note: "The verb is the interesting part: run to. A tower does no good to somebody standing outside it, and the proverb assumes movement toward something under conditions that make running necessary. The next verse contrasts it with the rich man's wealth, which he imagines is a wall — the point being that one of the two is imagined.",
    },
    {
      verse: {
        text: "You are my hiding place. You will preserve me from trouble. You will surround me with songs of deliverance. Selah.",
        ref: "Psalm 32:7",
      },
      note: "A hiding place is a specific kind of protection: it does not remove the pursuer, it removes you from view. The psalm's first half is about the physical misery of unconfessed sin, so the trouble in question is partly self-inflicted — which is worth knowing, because it makes this a verse about shelter that includes the guilty.",
    },
    {
      verse: {
        text: "Yahweh will keep you from all evil. He will keep your soul. Yahweh will keep your going out and your coming in, from this time forward, and forever more.",
        ref: "Psalm 121:7–8",
      },
      note: "The word keep appears three times in two verses and six times in the psalm — it is the psalm's whole subject. Going out and coming in is a Hebrew way of saying everything you do. This is a pilgrim song, sung on a dangerous road, and the promise is about the traveller rather than about the road being safe.",
    },
    {
      verse: {
        text: "Yahweh's angel encamps around those who fear him, and delivers them.",
        ref: "Psalm 34:7",
      },
      note: "Encamps is a military verb — a camp set around something, the way an army surrounds what it is guarding. Read verse 19 of the same psalm alongside it, which says the afflictions of the righteous are many. The psalm holds both without embarrassment, and so should anybody quoting verse 7.",
    },
    {
      verse: {
        text: "He who dwells in the secret place of the Most High will rest in the shadow of the Almighty. I will say of Yahweh, 'He is my refuge and my fortress; my God, in whom I trust.'",
        ref: "Psalm 91:1–2",
      },
      note: "The opening of the psalm most associated with protection, and the images are a hiding place and a shadow — both of which describe where you are rather than what is happening. Verse 15 of the same psalm says I will be with him in trouble, which is the sentence that keeps the rest of it honest and is never the one on the poster.",
    },
    {
      verse: {
        text: "No weapon that is formed against you will prevail; and you will condemn every tongue that rises against you in judgment. This is the heritage of Yahweh's servants, and their righteousness is of me,\" says Yahweh.",
        ref: "Isaiah 54:17",
      },
      note: "Widely quoted as a personal guarantee, and it is addressed to a nation being restored after exile — a corporate promise about the survival of a people rather than about individual outcomes. Note also that the weapons are expected to be formed. Nothing here says they will not exist.",
    },
    {
      verse: {
        text: "But the Lord is faithful, who will establish you and guard you from the evil one.",
        ref: "2 Thessalonians 3:3",
      },
      note: "The object of the guarding is specified, and it is not circumstances. Paul has just asked for prayer that he be delivered from unreasonable men, and the assurance he offers in return is about a different category of threat. The protection promised in the New Testament is more often spiritual than physical, and this verse is unusually explicit about which.",
    },
    {
      verse: {
        text: "Yahweh is good, a stronghold in the day of trouble; and he knows those who take refuge in him.",
        ref: "Nahum 1:7",
      },
      note: "Sitting inside a book that is otherwise three chapters of judgement on an empire, which is what makes it land. In the day of trouble is a concession stated in the same breath as the comfort, and the second clause — he knows those who take refuge — is about being recognised rather than about being extracted.",
    },
    {
      verse: {
        text: "God is our refuge and strength, a very present help in trouble. Therefore we won't be afraid, though the earth changes, though the mountains are shaken into the heart of the seas; though its waters roar and are troubled, though the mountains tremble with their swelling.",
        ref: "Psalm 46:1–3",
      },
      note: "The word translated very present carries the sense of being easily found — help that is nearby rather than help that is powerful, which is a different and more useful claim. And the three though clauses are the psalm conceding, in advance, that the worst case is on the table.",
    },
  ],
  pray: [
    {
      type: "h2",
      id: "the-reading-that-survives",
      text: "The reading that survives",
    },
    {
      type: "p",
      text: "Take the fortification imagery seriously and the whole category becomes usable. A rock, a tower, a shadow, a hiding place — each of them describes where you are while something is happening, and none of them describes the something not happening.",
    },
    {
      type: "p",
      text: "The alternative reading has a cost that is paid by somebody else. A person told these verses guarantee safety, whose child is then ill, has been set up to conclude that their faith failed at the worst possible moment — which is why {the correction on Psalm 91} matters more than it looks.",
      links: { "the correction on Psalm 91": "/verse/psalm-91" },
    },
    {
      type: "note",
      text: "The test: could you say the verse out loud, to the person in the worst version of the situation, without flinching? If not, you are holding the insurance reading.",
    },
  ],
  faq: [
    {
      q: "What does the Bible say about God's protection?",
      a: "It uses the vocabulary of shelter — rock, fortress, tower, hiding place, shield — which describes where you are during danger rather than the danger not arriving. Psalm 91:15 says it plainly: I will be with him in trouble.",
    },
    {
      q: "Which psalm is best for protection?",
      a: "Psalm 91 is the one most associated with it and Psalm 121 is the one to read alongside it. Both are short, and both are more honest about danger than the extracts usually printed from them.",
    },
    {
      q: "Which translation is quoted here?",
      a: "The World English Bible, which is in the public domain, so all ten are printed whole rather than referred to. Seven of them render the divine name as Yahweh — Psalm 18:2, Psalm 91:1–2 and Proverbs 18:10 among them — where most translations have 'the LORD'.",
    },
  ],
  related: ["phone-addiction-bible-verses", "how-to-stop-scrolling-at-night"],
  relatedTopics: [
    "bible-verses-for-anxiety-at-night",
    "bible-verses-about-peace",
  ],
};
